import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Helmet from "react-helmet";

import eduboxLogo from "../assets/img/EDUbox_cut_no_tag@2x.jpg";
import { fetchToolsGeneral } from "../generalpage/connect";
import { trackPageImpression } from "../generalpage/trackPageImpression";

const OverviewTools = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 95vw;
  margin: auto;
  padding-bottom: 15px;
  height: auto;
  gap: 10px;
  align-content: flex-start;
  justify-content: center;
  @media (max-width: 700px) {
    width: 100vw;
    gap: 5px;
    flex-direction: row;
  }
`;

const OverviewItem = styled.a`
  min-width: calc((95vw - (30px * 4)) / 5);
  padding: 20px 0;
  margin: 5px 5px;
  box-sizing: border-box;
  border-radius: 10px;
  color: #031037;
  background-color: white;
  font-weight: bold;
  box-shadow: 0px 0px 10px #cfcfcf;
  text-decoration: none;
  img {
    min-width: 300px;
  }
  &:hover {
    background-color: rgb(242, 242, 242);
  }
  @media (max-width: 1300px) {
    min-width: calc((95vw - (18px * 3)) / 2);
    padding: 10px 0;
    margin: 10px 11px;
  }
  @media (max-width: 700px) {
    min-width: 95%;
    padding: 10px 0;
    margin: 5px 11px;
  }
`;

const OverviewItemImage = styled.img`
  width: 200px;
  height: auto;
  background-color: white;
  padding: 15px;
  border-radius: 10px;
`;

const OverviewItemTitle = styled.h2`
  font-size: 1.7rem;
  padding-inline: 0;
  margin: 0;
`;

const SearchBar = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
  position: relative;
`;

const SearchForm = styled.form`
  display: flex;
  width: 30%;
  min-width: 360px;
  @media (max-width: 700px) {
    width: calc(100% - 22px);
    min-width: initial;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-right: 10px;
`;

const SearchInput = styled.input`
  padding: 10px;
  font-size: 1rem;
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-right: 10px;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
`;

const SearchButton = styled.button`
  padding: 10px;
  font-size: 1rem;
  border: none;
  background-color: #031037;
  color: white;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #022067;
  }
`;

const AutocompleteList = styled.ul`
  position: absolute;
  top: 44px;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 30%;
  min-width: 360px;
  max-height: 200px;
  overflow-y: auto;
  list-style-type: none;
  padding: 0;
  margin: 0;
  @media (max-width: 700px) {
    width: calc(100% - 22px);
    min-width: initial;
  }
`;

const AutocompleteItem = styled.li`
  padding: 10px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: #f0f0f0;
  }
`;

export default function Home() {
  const [fetchedToolsGeneral, setFetchedToolsGeneral] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredTools, setFilteredTools] = useState([]);
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  useEffect(() => {
    async function getTools() {
      const tools = await fetchToolsGeneral();
      const flattenedTools = tools.flat(Infinity); // Flattening the nested arrays
      setFetchedToolsGeneral(flattenedTools || []);
      setFilteredTools(flattenedTools || []);
    }
    getTools();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const toolname = url.searchParams.get("toolname") || "";
    trackPageImpression({
      boxname: "tools",
      pageid: toolname,
      lang: "nl-BE",
    });
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.toLowerCase();
    const filtered = fetchedToolsGeneral.filter((tool) =>
      tool.EDUbox.some(
        (item) => item.tool_name && item.tool_name.toLowerCase().includes(query)
      )
    );
    setFilteredTools(filtered);
  };

  const handleInputChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query.length > 0) {
      const uniqueToolNames = new Set();
      fetchedToolsGeneral.forEach((tool) => {
        tool.EDUbox.forEach((item) => {
          if (
            item.tool_name &&
            item.tool_name.toLowerCase().includes(query.toLowerCase())
          ) {
            uniqueToolNames.add(item.tool_name);
          }
        });
      });

      setAutocompleteOptions(Array.from(uniqueToolNames));
      setShowAutocomplete(true);
    } else {
      setShowAutocomplete(false);
    }
  };

  const handleAutocompleteClick = (toolName) => {
    setSearchQuery(toolName);
    setShowAutocomplete(false);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setFilteredTools(fetchedToolsGeneral);
    setShowAutocomplete(false);
  };

  return (
    <>
      <Helmet>
        <title>EDUbox: Interactieve tools</title>
        <meta
          name="keywords"
          content="EDUbox,lesmateriaal, lestool, VRT, interactieve tools"
        />
        <meta
          name="description"
          content="Dompel je klas onder in boeiende thema’s met interactieve leerervaringen op maat van jongeren, verrijkt met audiovisueel materiaal en VRT-experten."
        />
        <meta name="author" content="VRT" />
        <meta name="language" content="nl" />
        <meta name="robots" content="index,follow" />
        <meta property="og:author" content="https://www.facebook.com/vrt.be" />
        <meta property="og:title" content="EDUbox | VRT.be" />
        <meta property="og:description" content="edubox" />
        <meta property="og:type" content="home" />
        <meta
          property="og:url"
          content="https://www.vrt.be/nl/edubox/catalogus/"
        />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta http-equiv="cleartype" content="on" />
        <meta name="theme-color" content="#ffffff" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@vrt" />
        <meta name="twitter:creator" content="@vrt" />
        <meta name="twitter:title" content="EDUbox | VRT.be" />
        <meta
          name="twitter:description"
          content="Dompel je klas onder in boeiende thema’s met interactieve leerervaringen op maat van jongeren, verrijkt met audiovisueel materiaal en VRT-experten."
        />
        <meta
          name="twitter:url"
          content="https://www.vrt.be/nl/edubox/catalogus/"
        />
        <meta property="og:image" content="../assets/img/banner.jpeg" />
        <meta name="twitter:image" content="../assets/img/banner.jpeg" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="../assets/img/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="../assets/img/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="../assets/img/favicon-16x16.png"
        />
        <link rel="manifest" href="../assets/img/site.webmanifest" />
      </Helmet>
      <OverviewItemImage src={eduboxLogo} alt="EDUbox logo" />
      <p>EDUbox gebruikt in de EDUbox tal van tools.</p>
      <p>Dit overzicht bevat de tools ontwikkeld door VRT.</p>
      <SearchBar>
        <SearchForm onSubmit={handleSearch}>
          <SearchInputWrapper>
            <SearchInput
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              placeholder="Zoek tools per EDUbox"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch(e);
                }
              }}
            />
            {searchQuery && <ClearButton onClick={clearSearch}>×</ClearButton>}
          </SearchInputWrapper>
          <SearchButton type="submit">Zoeken</SearchButton>
        </SearchForm>
        {showAutocomplete && (
          <AutocompleteList>
            {autocompleteOptions.map((option, index) => (
              <AutocompleteItem
                key={index}
                onClick={() => handleAutocompleteClick(option)}
              >
                {option}
              </AutocompleteItem>
            ))}
          </AutocompleteList>
        )}
      </SearchBar>
      <OverviewTools>
        {filteredTools?.map((tool) => (
          <OverviewItem key={tool?.attributes.tool_id} href={tool?.toolUrl}>
            <OverviewItemImage
              src={tool?.banner?.url}
              alt={tool?.attributes.banner_alt}
            />
            <OverviewItemTitle>{tool?.attributes.tool_name}</OverviewItemTitle>
          </OverviewItem>
        ))}
      </OverviewTools>
    </>
  );
}
